import { MainLayout } from '@/components/layout/MainLayout';
import { LargeTitleHeader } from '@/components/common';
import { UpcomingJobs } from '@/components/dashboard/UpcomingJobs';
import { MiniCalendar } from '@/components/dashboard/MiniCalendar';
import { AlertsList } from '@/components/dashboard/AlertsList';
import { RevenueTrendChart } from '@/components/dashboard/RevenueTrendChart';
import { JobsPipeline } from '@/components/dashboard/JobsPipeline';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { OnboardingWizard } from '@/components/onboarding/OnboardingWizard';
import { useAuth } from '@/lib/hooks/useAuth';
import { useState, useEffect, useCallback } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useDesktopMode } from '@/lib/hooks/useDesktopMode';
import { useWindowed } from '@/lib/context/WindowManagerContext';
import { jobPath } from '@/lib/utils/navPaths';
import apiClient from '@/lib/api/client';

// Recently-viewed jobs stored in localStorage (#17)
const LS_RECENT_JOBS = 'dashboard_recentJobs';
type RecentJob = { id: number; jobNumber: string; title: string; status: string };

function getRecentJobs(): RecentJob[] {
  try { return JSON.parse(localStorage.getItem(LS_RECENT_JOBS) || '[]'); } catch { return []; }
}

export function trackRecentJob(job: RecentJob) {
  const list = getRecentJobs().filter(j => j.id !== job.id);
  list.unshift(job);
  localStorage.setItem(LS_RECENT_JOBS, JSON.stringify(list.slice(0, 5)));
}
import {
  getDashboardStats, getUpcomingJobs, getAlerts, getCalendarEvents,
  getRevenueTrend, getRecentActivity,
  getNeedsAttention, getDailyLogStatus, getJobTaskLists,
} from '@/lib/api/dashboard';
import { getSettings } from '@/lib/api/settings';
import type {
  DashboardStats, UpcomingJob, Alert, CalendarEvent, RevenueTrendPoint, ActivityItem,
  DailyLogJobStatus, JobTaskCard,
} from '@/lib/api/dashboard';
import { NeedsAttentionPanel } from '@/components/dashboard/NeedsAttentionPanel';
import { QuickDailyLog } from '@/components/dashboard/QuickDailyLog';
import { JobTaskLists } from '@/components/dashboard/JobTaskLists';
import { WeatherStrip, type DayForecast } from '@/components/dashboard/WeatherStrip';

const money = (n: number, cents = false) =>
  `$${(n || 0).toLocaleString('en-CA', {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  })}`;

export const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // CR #184 — see the redirect below the hooks
  const desktopMode = useDesktopMode();
  const windowed = useWindowed();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
  const [upcomingJobs, setUpcomingJobs] = useState<UpcomingJob[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [revenueTrend, setRevenueTrend] = useState<RevenueTrendPoint[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [needsAttention, setNeedsAttention] = useState<Alert[]>([]);
  const [forecast, setForecast] = useState<DayForecast[]>([]);
  const [dailyLogJobs, setDailyLogJobs] = useState<DailyLogJobStatus[]>([]);
  // CR #131 — toolbox-talk sign-offs still outstanding
  const [talkGaps, setTalkGaps] = useState<{ id: number; title: string; talk_date: string; crew: number; acked: number; unapproved: number }[]>([]);
  const [jobTasks, setJobTasks] = useState<JobTaskCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [error, setError] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner';

  useEffect(() => {
    loadDashboardData();
    setRecentJobs(getRecentJobs());
  }, []);

  // Dashboard keyboard shortcut: R = refresh (#24)
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement).isContentEditable;
    if (isInput) return;
    if ((e.key === 'r' || e.key === 'R') && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      loadDashboardData(true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (!isAdminOrOwner) return;
    getSettings().then((settings: any) => {
      const completed = settings?.general?.onboarding_completed;
      if (!completed) setShowOnboarding(true);
    }).catch(() => {});
  }, [isAdminOrOwner]);

  // silent=true → background refresh (no skeleton, keep showing current data on error)
  const loadDashboardData = async (silent = false) => {
    try {
      if (silent) setRefreshing(true); else setLoading(true);
      setError(false);
      const [statsData, jobsData, alertsData, eventsData, trendData, activityData, attentionData, logStatusData, jobTasksData] = await Promise.all([
        getDashboardStats(),
        getUpcomingJobs(),
        getAlerts(),
        getCalendarEvents(),
        getRevenueTrend().catch(() => [] as RevenueTrendPoint[]),
        getRecentActivity().catch(() => [] as ActivityItem[]),
        getNeedsAttention().catch(() => [] as Alert[]),
        getDailyLogStatus().catch(() => [] as DailyLogJobStatus[]),
        getJobTaskLists().catch(() => [] as JobTaskCard[]),
      ]);
      setStats(statsData);
      setUpcomingJobs(jobsData);
      setAlerts(alertsData);
      setEvents(eventsData);
      setRevenueTrend(trendData);
      setRecentActivity(activityData);
      setNeedsAttention(attentionData);
      setDailyLogJobs(logStatusData);
      apiClient.get('/safety/toolbox-talks/outstanding')
        .then((r: { data: { data?: typeof talkGaps } }) => setTalkGaps(r.data.data ?? []))
        .catch(() => {});
      setJobTasks(jobTasksData);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      if (!silent) setError(true);
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  };

  // Keep the dashboard live: poll every 30s and refresh when the tab regains focus.
  useEffect(() => {
    const id = setInterval(() => loadDashboardData(true), 30000);
    const onVis = () => { if (document.visibilityState === 'visible') loadDashboardData(true); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CR #184: with Desktop Mode on, the dashboard IS the OS desktop — wallpaper
  // with every applet as a widget. The classic page stays for windows (the
  // Dashboard icon opens it as an app) and for classic mode. This return sits
  // after every hook above, so hook order is stable (#310 lesson).
  if (desktopMode && !windowed) return <Navigate to="/desktop" replace />;

  const today = new Date().toLocaleDateString('en-CA', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  // CR #117: the old 4 big stat cards + 5 ops tiles are compacted into one
  // dense table (statRows) rendered BELOW the working row.
  const statRows = stats ? [
    { label: 'Active Jobs', value: String(stats.activeJobs), sub: `${stats.inProgressJobs} active · ${stats.scheduledJobs} scheduled`, icon: 'construction', to: '/jobs', critical: false },
    { label: 'Outstanding A/R', value: money(stats.arOutstanding), sub: `of ${money(stats.invoicedTotal)} invoiced`, icon: 'account_balance_wallet', to: '/invoices', critical: false },
    { label: 'Overdue Invoices', value: String(stats.overdueCount), sub: `${money(stats.overdueAmount)} past due`, icon: 'receipt_long', to: '/invoices', critical: stats.overdueCount > 0 },
    { label: 'Pending Estimates', value: String(stats.pendingEstimates), sub: `${money(stats.pendingEstimateValue)} in pipeline`, icon: 'request_quote', to: '/quotes', critical: false },
    { label: 'Completed Jobs', value: String(stats.completedJobs), sub: '', icon: 'task_alt', to: '/jobs', critical: false },
    { label: 'Scheduled', value: String(stats.upcomingScheduleCount), sub: 'upcoming visits', icon: 'event', to: '/calendar', critical: false },
    { label: 'Clients', value: String(stats.totalClients), sub: '', icon: 'groups', to: '/clients', critical: false },
    { label: 'Active Crew', value: String(stats.activeEmployees), sub: '', icon: 'engineering', to: '/employees', critical: false },
    { label: 'Catalog Items', value: stats.catalogProducts.toLocaleString('en-CA'), sub: '', icon: 'inventory_2', to: '/catalog', critical: false },
  ] : [];

  return (
    <MainLayout>
      {showOnboarding && <OnboardingWizard onComplete={() => setShowOnboarding(false)} />}
      <LargeTitleHeader
        title={`Welcome, ${user?.username || 'there'}`}
        subtitle={today}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* Live indicator */}
            <span className="hidden sm:flex items-center gap-1.5 px-2.5 h-9 rounded-lg bg-success/10 text-success text-xs font-semibold" title={lastUpdated ? `Last updated ${lastUpdated.toLocaleTimeString('en-CA')}` : 'Live'}>
              <span className={`w-1.5 h-1.5 rounded-full bg-success ${refreshing ? 'animate-ping' : 'animate-pulse'}`} />
              {lastUpdated ? `Live · ${lastUpdated.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' })}` : 'Live'}
            </span>
            <Link to="/jobs/new" className="flex h-9 items-center justify-center gap-1.5 rounded-lg px-3 bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors" title="Create new job">
              <span className="material-symbols-outlined text-base">add</span>
              <span className="hidden sm:inline">New Job</span>
            </Link>
            <Link to="/quotes/new" className="flex h-9 items-center justify-center gap-1.5 rounded-lg px-3 bg-white text-gray-900 border border-gray-200 text-sm font-bold hover:bg-gray-50 transition-colors" title="Create new quote">
              <span className="material-symbols-outlined text-base">request_quote</span>
              <span className="hidden sm:inline">New Quote</span>
            </Link>
            {/* Refresh shortcut hint (#25) */}
            <button
              onClick={() => loadDashboardData(true)}
              className="flex h-9 items-center justify-center gap-1.5 rounded-lg px-3 bg-white text-gray-600 border border-gray-200 text-sm font-medium hover:bg-gray-50 transition-colors"
              title="Refresh dashboard (R)"
            >
              <span className={`material-symbols-outlined text-base ${refreshing ? 'animate-spin' : ''}`}>refresh</span>
              <kbd className="hidden lg:inline-flex items-center justify-center bg-gray-100 rounded px-1 text-xs font-mono">R</kbd>
            </button>
          </div>
        }
      />

      {/* Quick actions — one tap from the dashboard into any create flow */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-none -mt-2">
        {[
          { to: '/jobs/new', icon: 'add_box', label: 'New job' },
          { to: '/clients?new=1', icon: 'person_add', label: 'New client' },
          { to: '/employees?new=1', icon: 'badge', label: 'New employee' },
          { to: '/invoices?new=1', icon: 'receipt_long', label: 'New invoice' },
          { to: '/quotes/wizard', icon: 'request_quote', label: 'New quote' },
          { to: '/planner', icon: 'view_kanban', label: 'Plan tasks' },
          { to: '/calendar', icon: 'more_time', label: 'Log crew hours' },
          { to: '/crews?new=1', icon: 'diversity_3', label: 'New crew' },
          { to: '/documents', icon: 'upload_file', label: 'Upload doc' },
        ].map(a => (
          <Link key={a.label} to={a.to}
            className="flex items-center gap-1.5 h-9 px-3 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700 whitespace-nowrap flex-shrink-0 hover:border-[#C77D3F]/50 hover:text-[#C77D3F] active:scale-95 transition-all">
            <span className="material-symbols-outlined text-base text-[#C77D3F]">{a.icon}</span>
            {a.label}
          </Link>
        ))}
      </div>

      {error && (
        <div className="flex items-center justify-between gap-4 rounded-lg border border-critical/30 bg-critical/5 p-4">
          <p className="text-sm font-medium text-critical">Couldn't load dashboard data.</p>
          <button onClick={() => loadDashboardData()} className="text-sm font-bold text-critical hover:underline">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-32 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 h-56 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
            <div className="h-56 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 h-48 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
            <div className="space-y-4">
              <div className="h-36 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
              <div className="h-36 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Working row FIRST (#99, #117): what you ACT on daily — jobs,
              tasks, daily log, weather, calendar, alerts — leads the page. */}
          {/* CR #125: on phones the two columns stack, so the right-hand
              column's weather/calendar ended up below the long job+task lists.
              `order-*` puts Erik's mobile sequence first — weather, calendar,
              tasks, daily log — while lg: restores the two-column desktop layout. */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="contents lg:block lg:col-span-2 lg:space-y-6">
              <div className="order-3 lg:order-none"><UpcomingJobs jobs={upcomingJobs} /></div>
              {/* Per-job expanded task lists (#87) */}
              <div className="order-4 lg:order-none lg:mt-6"><JobTaskLists tasks={jobTasks} onChanged={() => loadDashboardData(true)} /></div>
              {/* CR #195: the numbers table fills the left column under the
                  task lists — the right column is much taller, and this dead
                  space was a full screen of nothing on desktop. */}
              {statRows.length > 0 && (
                <div className="order-8 lg:order-none lg:mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100">
                    <span className="material-symbols-outlined text-base text-gray-400">monitoring</span>
                    <h3 className="text-sm font-bold text-gray-700">Numbers at a glance</h3>
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-gray-50">
                      {statRows.map(r => (
                        <tr key={r.label} onClick={() => navigate(r.to)} className="hover:bg-gray-50 cursor-pointer">
                          <td className="pl-4 pr-2 py-2 w-8">
                            <span className={`material-symbols-outlined text-lg leading-none ${r.critical ? 'text-critical' : 'text-gray-400'}`}>{r.icon}</span>
                          </td>
                          <td className="px-2 py-2 text-gray-600">{r.label}</td>
                          <td className={`px-2 py-2 text-right font-bold whitespace-nowrap ${r.critical ? 'text-critical' : 'text-gray-900'}`}>{r.value}</td>
                          <td className="pl-2 pr-4 py-2 text-right text-xs text-gray-400 hidden sm:table-cell whitespace-nowrap">{r.sub}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="contents lg:block lg:space-y-6">
              {/* 7-day site weather (#98) — feeds the calendar's risk tint */}
              <div className="order-1 lg:order-none"><WeatherStrip onForecast={setForecast} /></div>
              <div className="order-2 lg:order-none lg:mt-6"><MiniCalendar events={events} forecast={forecast} /></div>
              {/* Quick daily-log entry (#88) */}
              <div className="order-5 lg:order-none lg:mt-6"><QuickDailyLog jobs={dailyLogJobs} onLogged={() => loadDashboardData(true)} /></div>
              {/* CR #131 — safety sign-offs the crew still owes */}
              {talkGaps.some(t => t.acked < t.crew || t.unapproved > 0) && (
                <div className="order-5 lg:order-none lg:mt-6 bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold text-gray-700 flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-base text-amber-500">health_and_safety</span>
                      Safety sign-offs
                    </h3>
                    <Link to="/toolbox-talks" className="text-xs font-semibold text-primary">Open</Link>
                  </div>
                  <div className="space-y-1.5">
                    {talkGaps.filter(t => t.acked < t.crew || t.unapproved > 0).slice(0, 4).map(t => (
                      <Link key={t.id} to="/toolbox-talks" className="flex items-center gap-2 text-sm hover:bg-gray-50 rounded-lg -mx-1 px-1 py-0.5">
                        <span className="flex-1 min-w-0 truncate text-gray-700">{t.title}</span>
                        {t.unapproved > 0 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 whitespace-nowrap">
                            {t.unapproved} to approve
                          </span>
                        )}
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                          t.acked >= t.crew ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`}>
                          {t.acked}/{t.crew} signed
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              <div className="order-6 lg:order-none lg:mt-6"><AlertsList alerts={alerts} /></div>
              {/* Data-quality gaps (#77) */}
              <div className="order-7 lg:order-none lg:mt-6"><NeedsAttentionPanel items={needsAttention} /></div>
            </div>
          </div>

          {/* CR #117 table now lives inside the working row's left column (#195) */}
          {stats && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <RevenueTrendChart data={revenueTrend} />
              </div>
              <div>
                <JobsPipeline stats={stats} />
              </div>
            </div>
          )}

          {/* Recently viewed jobs (#17) */}
          {recentJobs.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-700 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-base text-gray-400">history</span>
                  Recently Viewed
                </h3>
                <button onClick={() => { localStorage.removeItem(LS_RECENT_JOBS); setRecentJobs([]); }} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {recentJobs.map(j => (
                  <button
                    key={j.id}
                    onClick={() => navigate(jobPath(undefined, j.id))}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 hover:border-primary/40 hover:bg-primary/5 transition-colors text-sm"
                  >
                    <span className="font-bold text-primary text-xs">{j.jobNumber}</span>
                    <span className="text-gray-700 max-w-[160px] truncate">{j.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <RecentActivity items={recentActivity} />
            </div>
            {stats && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-base font-bold text-gray-900 mb-3">Cash Flow</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Awaiting Payment</span>
                    <span className="text-sm font-bold text-gray-900">{money(stats.awaitingPayment)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Overdue</span>
                    <span className={`text-sm font-bold ${stats.overdueAmount > 0 ? 'text-critical' : 'text-gray-900'}`}>{money(stats.overdueAmount)}</span>
                  </div>
                  <div className="h-px bg-gray-100" />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Total Collected</span>
                    <span className="text-sm font-bold text-success">{money(stats.collectedTotal)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Total Invoiced</span>
                    <span className="text-sm font-bold text-gray-900">{money(stats.invoicedTotal)}</span>
                  </div>
                  {stats.invoicedTotal > 0 && (
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Collection rate</span>
                        <span>{Math.round((stats.collectedTotal / stats.invoicedTotal) * 100)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full bg-success rounded-full transition-all"
                          style={{ width: `${Math.min(100, (stats.collectedTotal / stats.invoicedTotal) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </MainLayout>
  );
};
